import { type FormEvent, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate } from 'react-router-dom'
import { workspaceApi, type Workspace } from '../api/workspaces'
import { useAuth } from '../auth/useAuth'

const headingFont = 'Lexend, Inter, sans-serif'
const bodyFont = 'Source Sans 3, Inter, sans-serif'

type TaxYearOption = {
  label: string
  taxYearStart: string
  taxYearEnd: string
}

function getNzTaxYearOptions(): TaxYearOption[] {
  const now = new Date()
  const currentStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
  const startYears = [currentStartYear - 1, currentStartYear, currentStartYear + 1]

  return startYears.map((year) => ({
    label: `${year}–${year + 1} NZ tax year`,
    taxYearStart: `${year}-04-01`,
    taxYearEnd: `${year + 1}-03-31`,
  }))
}

function formatDate(value?: string) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function WorkspaceCard({
  workspace,
  onOpen,
  onRename,
  onDelete,
  isRenaming,
  isDeleting,
}: {
  workspace: Workspace
  onOpen: () => void
  onRename: (workspaceId: string, nextName: string) => Promise<void>
  onDelete: (workspaceId: string) => Promise<void>
  isRenaming: boolean
  isDeleting: boolean
}) {
  const warningCount = workspace.reviewSummary?.warningCount || 0
  const highSeverityWarningCount = workspace.reviewSummary?.highSeverityWarningCount || 0
  const [nameDraft, setNameDraft] = useState(workspace.name || '')

  const handleRename = async (event: FormEvent) => {
    event.preventDefault()
    await onRename(workspace.id, nameDraft)
  }

  const handleDelete = async () => {
    const ok = window.confirm('Delete this workspace and all uploaded records in it? This cannot be undone.')
    if (!ok) return
    await onDelete(workspace.id)
  }

  return (
    <Card variant="outlined" sx={{ overflow: 'hidden', bgcolor: alpha('#ffffff', 0.78), borderColor: alpha('#123244', 0.08), boxShadow: 'none' }}>
      <CardActionArea onClick={onOpen}>
        <CardContent sx={{ p: { xs: 2.25, md: 2.75 } }}>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
            >
              <Box>
                <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', md: '1.2rem' } }}>
                  {workspace.name || `TaxHandoff NZ workspace ${workspace.id.slice(0, 8)}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                  Covers income from {workspace.taxYearStart} to {workspace.taxYearEnd}
                </Typography>
              </Box>
              <Chip
                label={workspace.status || 'unknown'}
                color={workspace.status === 'in_progress' ? 'primary' : 'default'}
                variant="outlined"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                Created: {formatDate(workspace.createdAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                Last updated: {formatDate(workspace.updatedAt)}
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                variant="outlined"
                label={`Your information ${workspace.metadata?.questionnaireAnsweredVisible || 0}/${workspace.metadata?.questionnaireTotalVisible || 0}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${workspace.metadata?.incomeEntryCount || 0} income entr${(workspace.metadata?.incomeEntryCount || 0) === 1 ? 'y' : 'ies'}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${workspace.metadata?.documentCount || 0} document${(workspace.metadata?.documentCount || 0) === 1 ? '' : 's'}`}
              />
              {warningCount > 0 ? (
                <Chip
                  size="small"
                  color={highSeverityWarningCount > 0 ? 'error' : 'warning'}
                  label={highSeverityWarningCount > 0
                    ? `${highSeverityWarningCount} priority item${highSeverityWarningCount === 1 ? '' : 's'}`
                    : `${warningCount} item${warningCount === 1 ? '' : 's'} to check`}
                />
              ) : (
                <Chip
                  size="small"
                  color={workspace.reviewSummary?.readiness?.status === 'strong' ? 'success' : 'default'}
                  label={workspace.reviewSummary?.readiness?.status === 'strong' ? 'Ready for accountant review' : 'Nothing open to check'}
                />
              )}
            </Stack>

            {workspace.reviewSummary?.warnings?.length ? (
              <Alert severity={highSeverityWarningCount > 0 ? 'error' : 'warning'} sx={{ bgcolor: alpha('#fffdf8', 0.88) }}>
                {workspace.reviewSummary.warnings[0]?.message}
              </Alert>
            ) : null}

            <Typography variant="body2" color="primary" fontWeight={700} sx={{ fontFamily: headingFont }}>
              Open and continue preparing this handoff →
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} onClick={(event) => event.stopPropagation()}>
              <Box component="form" onSubmit={handleRename} sx={{ display: 'flex', gap: 1, flex: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Workspace name"
                  value={nameDraft}
                  onChange={(event) => setNameDraft(event.target.value)}
                  placeholder="e.g. Sarah 2024-2025 return"
                />
                <Button type="submit" variant="outlined" disabled={isRenaming || nameDraft === (workspace.name || '')}>
                  {isRenaming ? 'Saving…' : 'Save'}
                </Button>
              </Box>
              <Button color="error" variant="outlined" disabled={isDeleting} onClick={handleDelete}>
                {isDeleting ? 'Deleting…' : 'Delete'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default function Workspaces() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isAuthenticated, isLoading, getUserEmail, logout } = useAuth()
  const taxYearOptions = useMemo(() => getNzTaxYearOptions(), [])
  const [selectedTaxYear, setSelectedTaxYear] = useState(taxYearOptions[1]?.taxYearStart || taxYearOptions[0].taxYearStart)
  const [newWorkspaceName, setNewWorkspaceName] = useState('')

  const selectedOption = taxYearOptions.find((option) => option.taxYearStart === selectedTaxYear) || taxYearOptions[0]

  const workspacesQuery = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspaceApi.list,
    enabled: isAuthenticated,
    staleTime: 30_000,
    retry: 2,
    retryDelay: 1_000,
  })

  const createWorkspace = useMutation({
    mutationFn: workspaceApi.create,
    onSuccess: async (workspace) => {
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      navigate(`/workspaces/${workspace.id}`)
    },
  })

  const renameWorkspace = useMutation({
    mutationFn: ({ workspaceId, name }: { workspaceId: string; name: string }) => workspaceApi.rename(workspaceId, name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
    },
  })

  const deleteWorkspaceMutation = useMutation({
    mutationFn: (workspaceId: string) => workspaceApi.remove(workspaceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
    },
  })

  const handleCreate = async () => {
    await createWorkspace.mutateAsync({
      taxYearStart: selectedOption.taxYearStart,
      taxYearEnd: selectedOption.taxYearEnd,
      name: newWorkspaceName.trim() || undefined,
    })
  }

  if (!isLoading && !isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: [
          'radial-gradient(circle at 10% 12%, rgba(8,145,178,0.12), transparent 22%)',
          'radial-gradient(circle at 90% 16%, rgba(5,150,105,0.10), transparent 18%)',
          'linear-gradient(180deg, #f9fbfc 0%, #f3f7f8 42%, #eef2f1 100%)',
        ].join(','),
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={{ xs: 3, md: 4 }} sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
        <Card sx={{ bgcolor: '#123244', color: 'white', overflow: 'hidden' }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2.5}>
              <Box>
                <Typography variant="h4" component="h1" gutterBottom sx={{ maxWidth: 620, fontSize: { xs: '2rem', sm: '2.5rem', md: '2.9rem' } }}>
                  TaxHandoff NZ Workspaces
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.86, fontFamily: bodyFont, maxWidth: 760 }}>
                  Signed in as {getUserEmail()} · Start one workspace for each NZ tax year, then gather the answers, records, and trial IR3 details your accountant will need.
                </Typography>
              </Box>
              <Button variant="outlined" color="inherit" onClick={() => logout()} sx={{ borderColor: 'rgba(255,255,255,0.35)', alignSelf: { xs: 'stretch', md: 'auto' } }}>
                Sign out
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ bgcolor: alpha('#ffffff', 0.72), border: `1px solid ${alpha('#0891b2', 0.16)}` }}>
          A workspace is your private area for one tax year. Pick the year first, then gather the answers, figures, and source documents you want ready for your accountant.
        </Alert>

        <Card sx={{ bgcolor: alpha('#fffdf8', 0.9) }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Create a tax workspace</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                Choose the NZ tax year first. In New Zealand, the tax year usually runs from 1 April to 31 March.
              </Typography>
              <TextField
                label="Workspace name (optional)"
                value={newWorkspaceName}
                onChange={(event) => setNewWorkspaceName(event.target.value)}
                helperText="Give it a clear label so you can find it later."
                fullWidth
              />
              <TextField
                select
                label="Tax year"
                value={selectedTaxYear}
                onChange={(event) => setSelectedTaxYear(event.target.value)}
                helperText="Example: 2024–2025 means income earned between 1 April 2024 and 31 March 2025."
                fullWidth
              >
                {taxYearOptions.map((option) => (
                  <MenuItem key={option.taxYearStart} value={option.taxYearStart}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                Selected range: {selectedOption.taxYearStart} to {selectedOption.taxYearEnd}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button variant="contained" onClick={handleCreate} disabled={createWorkspace.isPending}>
                  {createWorkspace.isPending ? 'Creating…' : 'Create workspace'}
                </Button>
                <Button variant="text" onClick={() => workspacesQuery.refetch()} disabled={workspacesQuery.isFetching}>
                  Refresh list
                </Button>
              </Stack>
              {createWorkspace.isError ? (
                <Alert severity="error">
                  Could not create workspace. {(createWorkspace.error as Error)?.message || 'Unknown error'}
                </Alert>
              ) : null}
              {renameWorkspace.isError ? (
                <Alert severity="error">
                  Could not rename workspace. {(renameWorkspace.error as Error)?.message || 'Unknown error'}
                </Alert>
              ) : null}
              {deleteWorkspaceMutation.isError ? (
                <Alert severity="error">
                  Could not delete workspace. {(deleteWorkspaceMutation.error as Error)?.message || 'Unknown error'}
                </Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="h5">Your existing workspaces</Typography>
            {workspacesQuery.isFetching ? <CircularProgress size={20} /> : null}
          </Stack>

          {workspacesQuery.isError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load workspaces. {(workspacesQuery.error as Error)?.message || 'Unknown error'}
            </Alert>
          ) : null}

          {workspacesQuery.isLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <Stack spacing={1} alignItems="center">
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                  Loading workspaces...
                </Typography>
              </Stack>
            </Box>
          ) : workspacesQuery.data && workspacesQuery.data.length > 0 ? (
            <Stack spacing={2}>
              {workspacesQuery.data.map((workspace) => (
                <WorkspaceCard
                  key={workspace.id}
                  workspace={workspace}
                  onOpen={() => navigate(`/workspaces/${workspace.id}`)}
                  onRename={async (workspaceId, nextName) => {
                    await renameWorkspace.mutateAsync({ workspaceId, name: nextName })
                  }}
                  onDelete={async (workspaceId) => {
                    await deleteWorkspaceMutation.mutateAsync(workspaceId)
                  }}
                  isRenaming={renameWorkspace.isPending}
                  isDeleting={deleteWorkspaceMutation.isPending}
                />
              ))}
            </Stack>
          ) : (
            <Alert severity="info" sx={{ bgcolor: alpha('#ffffff', 0.72) }}>No workspaces yet. Create your first accountant workspace above to start gathering the information and documents for your accountant.</Alert>
          )}
        </Box>
      </Stack>
    </Container>
    </Box>
  )
}
